import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { SavingsAccountMainComponent } from './savings-main/savings-account-main.component';
import { SavingsAccountConsentsComponent } from './savings-account-consents/savings-account-consents.component';
import { SavingsAccountIntroComponent } from './savings-account-intro/savings-account-intro.component';
import { SavingsAccountCreateComponent } from './savings-account-create/savings-account-create.component';
import { SavingsAccountVerificationComponent } from './savings-account-verification/savings-account-verification.component';
import {
  DocumentsKeyEnum,
  HasLegalDocsGuard,
  LegalDocumentsService,
  RequiredLegalDocumentsComponent,
  SharedLegalDocumentsModule,
} from '@brightside-web/shared/legal-documents';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';

import {
  FinancialAccountCreationErrorComponent,
  FinancialAccountTypes,
  FinancialAccountsCoreService,
  FinancialAccountsCreationService,
  AccountCreationSteps,
  FinancialAccountCreationGuard,
  FinancialCoreKYCComponent,
  FinancialAccountsHistoryService,
  FinancialCoreKYCVerificationInProgressComponent,
} from '@brightside-web/desktop/feature/financial-accounts-core';

import { HasSavingsAccountGuard } from './has-savings-account.guard';
import { NewSavingsAccountService } from './new-savings-account.service';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { TranslateModule } from '@ngx-translate/core';
import { SharedAccountModule } from '@brightside-web/shared/account';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { AutosaveSettingsComponent } from '@brightside-web/desktop/feature/autosave';
import { SavingsAccountTransactionHistoryComponent } from './savings-account-transaction-history/savings-account-transaction-history.component';
import { PlsService } from '@brightside-web/desktop/feature/prize-link-savings';

import { SavingsAccountStatementsComponent } from './savings-account-statements/savings-account-statements.component';
import { SavingsAutosaveIntroComponent } from './savings-autosave-intro/savings-autosave-intro.component';
import { SavingsTaxStatementsComponent } from './savings-tax-statements/savings-tax-statements.component';
import {
  authCanActivateGuard,
  FeatureFlagKillSwitchGuard,
} from '@brightside-web/desktop/data-access/core-services';
import { KnownFeatureFlags } from '@brightside-web/desktop/data-access/shared';

const productType = FinancialAccountTypes.SAVINGS;

const ROUTES: Routes = [
  {
    path: '',
    component: SavingsAccountMainComponent,
    children: [
      {
        path: 'creation',
        canActivate: [HasSavingsAccountGuard],
        data: {
          guardRedirectTo: ['../dashboard'],
        },
        children: [
          {
            path: AccountCreationSteps.INTRO,
            component: SavingsAccountIntroComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.INTRO },
          },
          {
            path: AccountCreationSteps.VERIFICATION,
            component: SavingsAccountVerificationComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.VERIFICATION },
          },
          {
            path: AccountCreationSteps.CONSENTS,
            component: SavingsAccountConsentsComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CONSENTS },
          },
          {
            path: AccountCreationSteps.CREATE,
            component: SavingsAccountCreateComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CREATE },
          },
          {
            path: AccountCreationSteps.KYC,
            component: FinancialCoreKYCComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { accountType: 'savings', creationStep: AccountCreationSteps.CREATE },
          },
          {
            path: AccountCreationSteps.KYC_VERIFICATION,
            component: FinancialCoreKYCVerificationInProgressComponent,
            data: { accountType: 'savings' },
          },
          {
            path: 'autosave_intro',
            component: SavingsAutosaveIntroComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CREATE },
          },
          {
            path: 'autosave',
            component: AutosaveSettingsComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: {
              routeOnSuccess: ['accounts/savings/dashboard'],
              routeOnBack: ['accounts/savings/dashboard'],
              creationStep: AccountCreationSteps.CREATE,
            },
          },
          {
            path: 'error',
            component: FinancialAccountCreationErrorComponent,
          },
          {
            path: '**',
            redirectTo: AccountCreationSteps.INTRO,
          },
        ],
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('@brightside-web/desktop-v3/feature/savings-dashboard').then(
            (m) => m.SavingsDashboardPageComponent
          ),
        canActivate: [HasSavingsAccountGuard, HasLegalDocsGuard],
        data: {
          pageConfig: {
            documentsKey: DocumentsKeyEnum.SAVINGS_ACCOUNT,
            legalRoute: ['accounts', 'savings', 'legal'],
          },
        },
      },
      {
        path: 'legal',
        component: RequiredLegalDocumentsComponent,
        canActivate: [HasSavingsAccountGuard],
        data: {
          pageConfig: {
            documentsKey: DocumentsKeyEnum.SAVINGS_ACCOUNT,
            pageOnForwardCtaPath: ['accounts', 'savings', 'dashboard'],
            showCloseButton: true,
            showBackbutton: false,
            closeButtonRoute: ['home'],
          },
        },
      },
      {
        path: 'autosave',
        component: AutosaveSettingsComponent,
        canActivate: [HasSavingsAccountGuard],
      },
      {
        path: 'transactions',
        component: SavingsAccountTransactionHistoryComponent,
        canActivate: [HasSavingsAccountGuard],
      },
      {
        path: 'statements',
        component: SavingsAccountStatementsComponent,
        canActivate: [authCanActivateGuard],
      },
      {
        path: 'tax-statements',
        component: SavingsTaxStatementsComponent,
        canActivate: [authCanActivateGuard],
      },
      {
        path: '**',
        redirectTo: 'dashboard',
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forChild(ROUTES),
    SharedDesktopModule,
    SharedLegalDocumentsModule,
    //SharedPrizeLinkSavingsModule,
    BrightsideUiModule,
    TranslateModule,
    SharedAccountModule,
    MicroUiMoleculeModule,
  ],
  declarations: [
    SavingsAccountMainComponent,
    SavingsAccountConsentsComponent,
    SavingsAccountIntroComponent,
    SavingsAccountCreateComponent,
    SavingsAccountVerificationComponent,
    SavingsAccountTransactionHistoryComponent,
    SavingsAccountStatementsComponent,
    SavingsAutosaveIntroComponent,
    SavingsTaxStatementsComponent,
  ],
  providers: [
    FinancialAccountsCreationService,
    FinancialAccountsCoreService,
    FinancialAccountsHistoryService,
    HasSavingsAccountGuard,
    FinancialAccountCreationGuard,
    LegalDocumentsService,
    HasLegalDocsGuard,
    PlsService,
  ],
})
export class DesktopFeatureSavingsAccountModule {}
